/**
 * LiveKit RNNoise noise-suppressor Track Processor.
 *
 * Implements LiveKit's TrackProcessor interface so that:
 *  1. RNNoise (deep-learning, GRU-based) denoising is applied transparently
 *     on the mic track — far better than Speex at filtering keyboard clicks,
 *     mouse clicks, and non-stationary background noise.
 *  2. A highpass filter removes low-frequency rumble (desk vibrations, HVAC,
 *     traffic) before it reaches RNNoise.
 *  3. A noise gate acts as a safety net during true silence so zero background
 *     hiss leaks through.
 *  4. Mute/unmute is handled via a GainNode ramp (not by toggling
 *     `mediaStreamTrack.enabled`, which crackles on AudioContext tracks).
 *  5. After publishing, `setTrackMuted` and `unmute` on the LiveKit track
 *     are overridden to call `processor.setMuted()` instead of toggling
 *     `enabled` or restarting the track.
 *
 * Audio pipeline:
 *   source -> highpass (85 Hz) -> RNNoise -> NoiseGate -> GainNode -> destination
 */

import type { TrackProcessor, AudioProcessorOptions } from 'livekit-client'
import { Track } from 'livekit-client'
import { loadRnnoise, NoiseGateWorkletNode, RnnoiseWorkletNode } from '@sapphi-red/web-noise-suppressor'
import rnnoiseWorkletPath from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url'
import noiseGateWorkletPath from '@sapphi-red/web-noise-suppressor/noiseGateWorklet.js?url'
import rnnoiseWasmPath from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'
import rnnoiseWasmSimdPath from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'

let rnnoiseWasmBinary: ArrayBuffer | null = null

/** Duration (seconds) for gain fade on mute/unmute — short but smooth. */
const FADE_DURATION = 0.05

/** Highpass filter cutoff frequency in Hz — removes low-frequency rumble. */
const HIGHPASS_FREQUENCY = 85

/** Highpass filter Q factor — 0.707 is Butterworth (maximally flat). */
const HIGHPASS_Q = 0.707

async function ensureWorkletsLoaded(ctx: AudioContext): Promise<boolean> {
  try {
    if (rnnoiseWasmBinary === null) {
      // loadRnnoise auto-selects SIMD when the browser supports it
      rnnoiseWasmBinary = await loadRnnoise({ url: rnnoiseWasmPath, simdUrl: rnnoiseWasmSimdPath })
    }
    await ctx.audioWorklet.addModule(rnnoiseWorkletPath)
    await ctx.audioWorklet.addModule(noiseGateWorkletPath)
    return true
  } catch (err) {
    console.warn('[DenoiserProcessor] Failed to load worklets:', err)
    return false
  }
}

/**
 * LiveKit-compatible Track Processor that applies RNNoise deep-learning
 * noise suppression and provides crackle-free mute/unmute via a GainNode.
 *
 * Usage:
 *   const pub = await room.localParticipant.setMicrophoneEnabled(true, rawOpts)
 *   const processor = new SpeexNoiseProcessor()  // class name kept for backward compat
 *   await (pub.track as LocalAudioTrack).setProcessor(processor)
 *   processor.patchTrackMute(pub.track as LocalAudioTrack)   // override setTrackMuted
 */
export class SpeexNoiseProcessor implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  name = 'rnnoise-noise-suppressor'
  processedTrack?: MediaStreamTrack

  private ctx?: AudioContext
  private source?: MediaStreamAudioSourceNode
  private highpass?: BiquadFilterNode
  private rnnoise?: RnnoiseWorkletNode
  private noiseGate?: NoiseGateWorkletNode
  private gainNode?: GainNode
  private destination?: MediaStreamAudioDestinationNode
  private fallback = false
  private inputVolume = 1.0

  async init(opts: AudioProcessorOptions): Promise<void> {
    // Create our own AudioContext for RNNoise worklets
    this.ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()

    if (this.ctx.state === 'suspended') {
      await this.ctx.resume()
    }

    const loaded = await ensureWorkletsLoaded(this.ctx)
    if (!loaded || !rnnoiseWasmBinary) {
      console.warn('[DenoiserProcessor] RNNoise unavailable — falling back to passthrough')
      this.fallback = true
      this.processedTrack = opts.track
      return
    }

    this.buildPipeline(opts.track)
  }

  async restart(opts: AudioProcessorOptions): Promise<void> {
    if (this.fallback || !this.ctx || !this.highpass) {
      this.processedTrack = opts.track
      return
    }

    // Disconnect old source, rebuild with new track
    if (this.source) {
      try { this.source.disconnect() } catch { /* already disconnected */ }
    }
    this.source = this.ctx.createMediaStreamSource(new MediaStream([opts.track]))
    this.source.connect(this.highpass)
    // processedTrack stays the same (same destination node)
  }

  async destroy(): Promise<void> {
    try {
      this.source?.disconnect()
      this.highpass?.disconnect()
      if (this.rnnoise) {
        this.rnnoise.destroy()
        this.rnnoise.disconnect()
      }
      this.noiseGate?.disconnect()
      this.gainNode?.disconnect()
      if (this.destination) {
        this.destination.stream.getTracks().forEach((t) => t.stop())
      }
    } catch (err) {
      console.warn('[DenoiserProcessor] Error during destroy:', err)
    }
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {})
    }
    this.processedTrack = undefined
    this.source = undefined
    this.highpass = undefined
    this.rnnoise = undefined
    this.noiseGate = undefined
    this.gainNode = undefined
    this.destination = undefined
    this.ctx = undefined
  }

  // ── public API ───────────────────────────────────────────────────────

  /**
   * SC-304: resume the denoiser's AudioContext if the browser suspended it
   * (e.g. while the tab was backgrounded). Idempotent — no-op if already
   * running or if the context was destroyed.
   */
  async resumeContext(): Promise<void> {
    if (this.ctx && this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume()
        console.log('[DenoiserProcessor] AudioContext resumed')
      } catch (e) {
        console.warn('[DenoiserProcessor] Failed to resume AudioContext:', e)
      }
    }
  }

  /**
   * Smoothly mute / unmute via the GainNode.
   * Ramps to 0 (mute) or 1 (unmute) over FADE_DURATION — no pops.
   */
  setMuted(muted: boolean): void {
    if (!this.gainNode || !this.ctx) return
    const now = this.ctx.currentTime
    this.gainNode.gain.cancelScheduledValues(now)
    this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now)
    this.gainNode.gain.linearRampToValueAtTime(muted ? 0 : this.inputVolume, now + FADE_DURATION)
  }

  /**
   * Set the input volume calibration.
   * Useful to boost or reduce mic volume before sending to LiveKit.
   * @param volume Multiplier (e.g., 1.0 is default, 2.0 is double)
   */
  setVolume(volume: number): void {
    this.inputVolume = volume
    // Immediately apply if we are not currently muted. We assume track is passed into patchTrackMute.
    // If we don't have a direct reference to isMuted, we can check the current gain target,
    // but the easiest is just setting it if it's currently > 0.
    if (!this.gainNode || !this.ctx) return
    // If it's already muted, the gain is 0, so changing inputVolume won't break anything.
    // If it's near 0, we shouldn't bump it.
    if (this.gainNode.gain.value > 0.01) {
      const now = this.ctx.currentTime
      this.gainNode.gain.cancelScheduledValues(now)
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now)
      this.gainNode.gain.linearRampToValueAtTime(this.inputVolume, now + FADE_DURATION)
    }
  }

  /**
   * Monkey-patch the LiveKit track's `setTrackMuted` **and** `unmute` so
   * that mute / unmute uses the GainNode instead of toggling
   * `mediaStreamTrack.enabled` and so that `unmute()` does NOT call
   * `restartTrack()`.
   *
   * Why `unmute()` must be patched:
   *   LiveKit's `LocalAudioTrack.unmute()` checks if the device has changed
   *   by comparing `this._constraints.deviceId` with
   *   `this.mediaStreamTrack.getConstraints().deviceId`.  The getter
   *   `.mediaStreamTrack` returns the processor's **destination** track,
   *   whose `.getConstraints()` returns `{}` (no deviceId).  This makes
   *   `deviceHasChanged` *always true*, causing `restartTrack()` on every
   *   unmute — which recreates getUserMedia streams + worklets and produces
   *   the "DenoiserWorklet.constructor" flood the user sees.
   *
   * Call this ONCE after `setProcessor()`.
   */
  patchTrackMute(track: {
    isMuted: boolean
    emit: (event: string, ...args: unknown[]) => boolean
    setTrackMuted: (muted: boolean) => void
    unmute: () => Promise<unknown>
  }): void {
    const processor = this

    // Override setTrackMuted: fade gain, don't toggle enabled
    track.setTrackMuted = function (muted: boolean) {
      if (this.isMuted === muted) return
      this.isMuted = muted
      // Fade gain — no enabled toggle, no crackle
      processor.setMuted(muted)
      // Emit event so LiveKit signals the server and updates UI
      this.emit(muted ? 'muted' : 'unmuted', this)
    }

    // Override unmute: just call setTrackMuted(false).
    // Skip the restartTrack() path entirely — the source track is still
    // alive and the device hasn't really changed.
    track.unmute = async function () {
      if (!this.isMuted) return this
      this.setTrackMuted(false)
      return this
    }
  }

  // ── internal ─────────────────────────────────────────────────────────
  private buildPipeline(sourceTrack: MediaStreamTrack): void {
    if (!this.ctx || !rnnoiseWasmBinary) return

    this.source = this.ctx.createMediaStreamSource(new MediaStream([sourceTrack]))

    // Highpass filter: removes low-frequency rumble (desk vibrations, HVAC,
    // traffic noise) before it reaches RNNoise.
    this.highpass = this.ctx.createBiquadFilter()
    this.highpass.type = 'highpass'
    this.highpass.frequency.value = HIGHPASS_FREQUENCY
    this.highpass.Q.value = HIGHPASS_Q

    // RNNoise: deep-learning noise suppression (GRU-based).
    // Dramatically better than Speex at filtering keyboard clicks, mouse
    // clicks, typing noise, and non-stationary background sounds.
    this.rnnoise = new RnnoiseWorkletNode(this.ctx, {
      wasmBinary: rnnoiseWasmBinary,
      maxChannels: 1,
    })

    // Noise gate: safety net that silences the output during true silence
    // so zero residual hiss leaks through.
    this.noiseGate = new NoiseGateWorkletNode(this.ctx, {
      openThreshold: -40,
      closeThreshold: -50,
      holdMs: 150,
      maxChannels: 1,
    })

    this.gainNode = this.ctx.createGain()
    this.destination = this.ctx.createMediaStreamDestination()

    // source -> highpass -> rnnoise -> noiseGate -> gainNode -> destination
    this.source.connect(this.highpass)
    this.highpass.connect(this.rnnoise)
    this.rnnoise.connect(this.noiseGate)
    this.noiseGate.connect(this.gainNode)
    this.gainNode.connect(this.destination)

    this.gainNode.gain.value = this.inputVolume

    this.processedTrack = this.destination.stream.getAudioTracks()[0]
  }
}
